/**
 * Event-related types
 */

import type { Id, TableNames } from "@/packages/types";

/**
 * Entity types for the event system.
 * Uses actual table names for type safety, plus "system" and "raw" for internal use.
 */
export type EntityType = TableNames | "system" | "raw";

export type BaseEvent = {
  id: string; // TODO: Add events table to schema
  eventType: EventType;
  entityType: EntityType;
  entityId: Id<TableNames>;
  triggeredBy: Id<"users">;
  timestamp: number;
  source: string;
  eventData: Record<string, unknown>;
};

/**
 * Document lifecycle event data
 */
export type DocumentEventData = {
  documentId: Id<"documents">;
  fileName: string;
  fileType: string;
  storageId?: Id<"_storage">;
  contentHash?: string;
  summaryLength?: number;
  errorType?: string;
};

/**
 * Audit lifecycle event data
 */
export type AuditEventData = {
  auditId: Id<"audits">;
  auditCode: string;
  auditName: string;
  status?: string;
  checklistItemCount?: number;
};

/**
 * Checklist event data
 */
export type ChecklistEventData = {
  checklistItemId: Id<"checklistItems">;
  auditId: Id<"audits">;
  clause: string;
  status?: string;
  documentId?: Id<"documents">;
};

/**
 * Event filter for querying
 */
export type EventFilters = {
  eventType?: EventType[];
  entityType?: EntityType;
  entityId?: Id<TableNames>;
  triggeredBy?: Id<"users">;
  startTime?: number;
  endTime?: number;
};

/**
 * Event categories for organization
 */
export const EVENT_CATEGORIES = {
  DOCUMENT_LIFECYCLE: "document_lifecycle" as const,
  DOCUMENT_TAG: "document_tag" as const,
  AUDIT_LIFECYCLE: "audit_lifecycle" as const,
  CHECKLIST: "checklist" as const,
  USER: "user" as const,
};

export type EventCategory =
  (typeof EVENT_CATEGORIES)[keyof typeof EVENT_CATEGORIES];

export const EVENT_TYPES = {
  // Document Lifecycle Events (9)
  DOCUMENT_UPLOADED: "document.uploaded",
  DOCUMENT_SUMMARY_GENERATED: "document.summary.generated",
  DOCUMENT_SUMMARY_UPDATED: "document.summary.updated",
  DOCUMENT_SUMMARY_REGENERATED: "document.summary.regenerated",
  DOCUMENT_SUMMARY_REGENERATION_FAILED: "document.summary.regeneration.failed",

  DOCUMENT_TAG_ASSIGNED: "document.tag.assigned",
  DOCUMENT_TAG_REMOVED: "document.tag.removed",
  DOCUMENT_UPDATED: "document.updated",
  DOCUMENT_DELETED: "document.deleted",
  DOCUMENT_TEXT_EXTRACTION_FAILED: "document.text.extraction.failed",
  DOCUMENT_SUMMARY_FAILED: "document.summary.failed",
  DOCUMENT_IMAGE_DESCRIPTION_FAILED: "document.image.description.failed",

  // Document Category Events (3)
  DOCUMENT_CATEGORY_CREATED: "category.created",
  DOCUMENT_CATEGORY_RENAMED: "category.renamed",
  DOCUMENT_CATEGORY_DELETED: "category.deleted",

  // Document Tag Events (3)
  DOCUMENT_TAG_GENERATION_STARTED: "document.tag_generation.started",
  DOCUMENT_TAG_GENERATION_COMPLETED: "document.tag_generation.completed",
  DOCUMENT_TAG_GENERATION_FAILED: "document.tag_generation.failed",

  // Audit Lifecycle Events (8)
  AUDIT_CREATED: "audit.created",
  AUDIT_UPDATED: "audit.updated",
  AUDIT_STATUS_CHANGED: "audit.status_changed",
  AUDIT_SUBMITTED: "audit.submitted",
  AUDIT_APPROVED: "audit.approved",
  AUDIT_REJECTED: "audit.rejected",
  AUDIT_ARCHIVED: "audit.archived",
  AUDIT_UNARCHIVED: "audit.unarchived",

  // Audit Checklist Events (6)
  AUDIT_CHECKLIST_GENERATION_STARTED: "audit.checklist_generation.started",
  AUDIT_CHECKLIST_GENERATION_COMPLETED: "audit.checklist_generation.completed",
  AUDIT_CHECKLIST_GENERATION_FAILED: "audit.checklist_generation.failed",
  AUDIT_CHECKLIST_AI_GENERATION_STARTED:
    "audit.checklist_ai_generation.started",
  AUDIT_CHECKLIST_AI_GENERATION_COMPLETED:
    "audit.checklist_ai_generation.completed",
  AUDIT_CHECKLIST_SAVING_STARTED: "audit.checklist_saving.started",
  AUDIT_CHECKLIST_SAVING_COMPLETED: "audit.checklist_saving.completed",
  AUDIT_CHECKLIST_SAVING_FAILED: "audit.checklist_saving.failed",

  CHECKLIST_ITEM_CREATED: "checklist_item.created",
  CHECKLIST_ITEM_UPDATED: "checklist_item.updated",
  CHECKLIST_ITEM_DELETED: "checklist_item.deleted",

  // Clause Research Events (3)
  CHECKLIST_CLAUSE_RESEARCH_STARTED: "audit.clause_research.started",
  CHECKLIST_CLAUSE_RESEARCH_COMPLETED: "audit.clause_research.completed",
  CHECKLIST_CLAUSE_RESEARCH_FAILED: "audit.clause_research.failed",

  // Clause Information Events (3)
  CHECKLIST_CLAUSE_INFORMATION_GENERATION_STARTED:
    "audit.clause_information_generation.started",
  CHECKLIST_CLAUSE_INFORMATION_GENERATION_COMPLETED:
    "audit.clause_information_generation.completed",
  CHECKLIST_CLAUSE_INFORMATION_GENERATION_FAILED:
    "audit.clause_information_generation.failed",

  // Clause Examples Events (3)
  CHECKLIST_CLAUSE_EXAMPLES_GENERATION_STARTED:
    "audit.clause_examples_generation.started",
  CHECKLIST_CLAUSE_EXAMPLES_GENERATION_COMPLETED:
    "audit.clause_examples_generation.completed",
  CHECKLIST_CLAUSE_EXAMPLES_GENERATION_FAILED:
    "audit.clause_examples_generation.failed",

  // Audit Summary Events (3)
  AUDIT_SUMMARY_GENERATION_STARTED: "audit.summary_generation.started",
  AUDIT_SUMMARY_GENERATION_COMPLETED: "audit.summary_generation.completed",
  AUDIT_SUMMARY_GENERATION_FAILED: "audit.summary_generation.failed",

  // Document-Checklist Link Events (2)
  DOCUMENT_CHECKLIST_LINKED: "document.checklist.linked",
  DOCUMENT_CHECKLIST_UNLINKED: "document.checklist.unlinked",

  // Checklist Item Events (8)
  CHECKLIST_ITEM_STATUS_CHANGED: "checklist_item.status_changed",
  CHECKLIST_ITEM_NOTES_UPDATED: "checklist_item.notes_updated",
  CHECKLIST_ITEM_SUMMARY_GENERATION_STARTED:
    "checklist_item.summary_generation.started",
  CHECKLIST_ITEM_SUMMARY_GENERATION_COMPLETED:
    "checklist_item.summary_generation.completed",
  CHECKLIST_ITEM_SUMMARY_GENERATION_FAILED:
    "checklist_item.summary_generation.failed",
  CHECKLIST_ITEM_RELATED_DOCUMENTS_SEARCH_STARTED:
    "checklist-item.related-documents.search-started",
  CHECKLIST_ITEM_RELATED_DOCUMENTS_FOUND:
    "checklist-item.related-documents.found",
  CHECKLIST_ITEM_RELATED_DOCUMENTS_SEARCH_FAILED:
    "checklist-item.related-documents.search-failed",
  CHECKLIST_ITEM_INFORMATION_GENERATION_STARTED:
    "checklist_item.information_generation.started",
  CHECKLIST_ITEM_INFORMATION_GENERATION_COMPLETED:
    "checklist_item.information_generation.completed",
  CHECKLIST_ITEM_INFORMATION_GENERATION_FAILED:
    "checklist_item.information_generation.failed",
  CHECKLIST_ITEM_EXAMPLES_GENERATION_STARTED:
    "checklist_item.examples_generation.started",
  CHECKLIST_ITEM_EXAMPLES_GENERATION_COMPLETED:
    "checklist_item.examples_generation.completed",
  CHECKLIST_ITEM_EXAMPLES_GENERATION_FAILED:
    "checklist_item.examples_generation.failed",
  CHECKLIST_ITEM_COMPLETED: "checklist-item.completed",
  CHECKLIST_ITEM_UNCOMPLETED: "checklist-item.uncompleted",
  CHECKLIST_ITEM_QUESTION_ANSWERED: "checklist_item.question.answered",
  CHECKLIST_ITEM_QUESTION_CLEARED: "checklist_item.question.cleared",

  // Auto-Link Events (2)
  AUTO_LINK_SUGGESTIONS_STARTED: "auto_link.suggestions.started",
  AUTO_LINK_SUGGESTIONS_GENERATED: "auto_link.suggestions.generated",

  // Audit Tags Events (1)
  AUDIT_TAGS_UPDATED: "audit.tags.updated",

  // Document Question Events (3)
  DOCUMENT_QUESTION_ASKED: "document.question.asked",
  DOCUMENT_QUESTION_ANSWERED: "document.question.answered",
  DOCUMENT_QUESTION_FAILED: "document.question.failed",

  // Document RAG Events (2)
  DOCUMENT_INDEXED_FOR_RAG: "document.rag.indexed",
  DOCUMENT_RAG_INDEXING_FAILED: "document.rag.indexing_failed",

  // Document Checklist Matching Events (3)
  DOCUMENT_CHECKLIST_MATCHING_STARTED: "document.checklist_matching.started",
  DOCUMENT_CHECKLIST_MATCHING_COMPLETED:
    "document.checklist_matching.completed",
  DOCUMENT_CHECKLIST_MATCHING_FAILED: "document.checklist_matching.failed",

  // Document Versioning Events (2)
  DOCUMENT_VERSION_CREATED: "document.version.created",
  DOCUMENT_DIFF_SUMMARY_GENERATED: "document.diff_summary.generated",

  // Finding Events (8)
  FINDING_CREATED: "finding.created",
  FINDING_CONFIRMED: "finding.confirmed",
  FINDING_DISCARDED: "finding.discarded",
  FINDING_UPDATED: "finding.updated",
  FINDING_RESOLVED: "finding.resolved",
  FINDING_REOPENED: "finding.reopened",
  FINDINGS_GENERATION_COMPLETED: "findings.generation.completed",
  FINDINGS_ENRICHMENT_COMPLETED: "findings.enrichment.completed",

  // User Events (1)
  USER_CREATED: "user.created",
  USER_UPDATED: "user.updated",
  USER_LOGGED_IN: "user.logged_in",

  // Notification Events (4)
  NOTIFICATION_CREATED: "notification.created",
  NOTIFICATION_READ: "notification.read",
  NOTIFICATION_ARCHIVED: "notification.archived",
  DOCUMENT_EXPIRATION_WARNING: "document.expiration_warning",
} as const;

/**
 * Event type union type
 * Represents all possible event type values
 */
export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];
